// src/components/Admin/ChatManager/Chat/MessageLogTable.jsx
import { useEffect, useState, useCallback } from 'react';
import {
    Table, TableHead, TableRow, TableCell,
    TableBody, Typography, Paper,
    Box, Pagination, useMediaQuery,
    IconButton, Tooltip, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;
const EXPORT_LIMIT = 5000;

export default function MessageLogTable({
    categoryId,
    categoryName = '',
    keyword,
    startDate,
    endDate,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const loadLogs = useCallback(() => {
        const isAll = categoryId === 'all';
        const url = isAll
            ? '/api/chat/logs/all/'
            : `/api/chat/categories/${categoryId}/logs/`;

        const params = {
            page,
            page_size: PAGE_SIZE,
            keyword,
            start_date: startDate,
            end_date: endDate,
        };

        api.get(url, { params })
            .then(res => {
                const results = res.data.results || res.data.logs || [];
                setLogs(results);
                setTotalCount(res.data.count || results.length);
                setLoading(false);
            })
            .catch(() => {
                alert('메시지 로그를 불러오지 못했습니다.');
                setLoading(false);
            });
    }, [categoryId, keyword, startDate, endDate, page]);

    useEffect(() => {
        setLoading(true);
        loadLogs();
    }, [loadLogs]);

    const toggleBlind = (id, blind) => {
        const url = `/api/chat/messages/${id}/${blind ? 'blind' : 'unblind'}/`;
        const txt = blind ? '블라인드 처리' : '블라인드 해제';
        if (!window.confirm(`${txt} 하시겠습니까?`)) return;

        api.post(url)
            .then(() => {
                alert(`${txt} 완료`);
                setLogs(prev =>
                    prev.map(m => (m.id === id ? { ...m, is_blinded: blind } : m))
                );
            })
            .catch(() => alert(`${txt} 실패`));
    };

    const exportToExcel = useCallback(() => {
        const isAll = categoryId === 'all';
        const url = isAll
            ? '/api/chat/logs/all/'
            : `/api/chat/categories/${categoryId}/logs/`;

        api.get(url, {
            params: {
                page: 1,
                page_size: EXPORT_LIMIT,
                keyword,
                start_date: startDate,
                end_date: endDate,
            },
        })
            .then(res => {
                const data = res.data.results || res.data.logs || [];

                const rows = data.map(m => [
                    m.category || categoryName || '',
                    m.is_anonymous_chat
                        ? `${m.sender_username || '익명'} (${m.display_name || ''})`
                        : m.display_name || m.sender_username || '',
                    m.content,
                    new Date(m.created_at).toLocaleString('ko-KR'),
                    m.is_blinded ? 'Y' : 'N',
                ]);

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet([
                    ['채팅방', '닉네임', '내용', '시간', '블라인드'],
                    ...rows,
                ]);
                ws['!cols'] = [
                    { wch: 18 },
                    { wch: 20 },
                    { wch: 60 },
                    { wch: 22 },
                    { wch: 10 },
                ];
                XLSX.utils.book_append_sheet(wb, ws, 'Logs');

                const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                saveAs(
                    new Blob([blob], { type: 'application/octet-stream' }),
                    `chat_logs_${Date.now()}.xlsx`,
                );
            })
            .catch(() => alert('엑셀 다운로드에 실패했습니다.'));
    }, [categoryId, categoryName, keyword, startDate, endDate]);

    useEffect(() => {
        const handler = () => exportToExcel();
        window.addEventListener('exportToExcel', handler);
        return () => window.removeEventListener('exportToExcel', handler);
    }, [exportToExcel]);

    if (loading)
        return <Typography sx={{ p: 2 }}>불러오는 중...</Typography>;

    return (
        <Paper sx={{ p: 2, overflowX: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                메시지 로그
            </Typography>

            <Table
                size="small"
                sx={{ tableLayout: 'fixed', wordBreak: 'break-word', minWidth: 360, }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            align="center"
                            sx={{
                                width: 140,
                                whiteSpace: 'nowrap',
                                display: { xs: 'none', sm: 'table-cell' },
                            }}
                        >
                            채팅방
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{ width: { xs: 100, sm: 180 }, whiteSpace: 'nowrap' }}
                        >
                            닉네임
                        </TableCell>
                        <TableCell align="left">내용</TableCell>
                        <TableCell
                            align="center"
                            sx={{ width: { xs: 120, sm: 160 }, whiteSpace: 'nowrap' }}
                        >
                            시간
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                width: 80,
                                minWidth: 80,
                                whiteSpace: 'nowrap',
                                display: { xs: 'none', md: 'table-cell' },
                            }}
                        >
                            관리
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {logs.map(m => {
                        const room = m.category || categoryName || '알 수 없음';
                        const nick = m.sender_username || '알 수 없음';
                        const disp = m.is_anonymous_chat
                            ? `${nick} (${m.display_name || ''})`
                            : nick;
                        const text = m.is_blinded
                            ? <i style={{ color: 'gray' }}>숨겨진 메시지</i>
                            : m.content.length > 50
                                ? `${m.content.slice(0, 50)}…`
                                : m.content;


                        const dateStr = dayjs(m.created_at).format('YYYY-MM-DD');
                        const timeStr = dayjs(m.created_at).format('A hh:mm:ss');
                        const dateCell = (
                            <>
                                <div>{dateStr}</div>
                                <div>{timeStr}</div>
                            </>
                        );

                        return (
                            <TableRow
                                key={m.id}
                                sx={{
                                    ...(m.is_blinded && { bgcolor: '#f0f0f0' }),
                                    '& td': {
                                        px: { xs: 0.5, sm: 1 },
                                        py: 0.5,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    },
                                }}
                            >
                                <TableCell
                                    align="center"
                                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                                >
                                    {room}
                                </TableCell>

                                <TableCell align="center">{disp}</TableCell>

                                <TableCell
                                    align="left"
                                    sx={{
                                        maxWidth: 300,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                                    }}
                                >
                                    {text}
                                </TableCell>

                                <TableCell align="center">{dateCell}</TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: 80,
                                        minWidth: 80,
                                        whiteSpace: 'nowrap',
                                        display: { xs: 'none', md: 'table-cell' },
                                    }}
                                >
                                    {m.is_blinded ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => toggleBlind(m.id, false)}
                                        >
                                            해제
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => toggleBlind(m.id, true)}
                                        >
                                            블라인드
                                        </Button>
                                    )}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{ display: { xs: 'table-cell', md: 'none' }, px: 0 }}
                                >
                                    <Tooltip title={m.is_blinded ? '숨김 해제' : '블라인드'}>
                                        <IconButton
                                            size="small"
                                            color={m.is_blinded ? 'primary' : 'error'}
                                            onClick={() => toggleBlind(m.id, !m.is_blinded)}
                                        >
                                            {m.is_blinded
                                                ? <VisibilityIcon fontSize="small" />
                                                : <VisibilityOffIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={Math.ceil(totalCount / PAGE_SIZE)}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                />
            </Box>
        </Paper>
    );
}
